import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import FilterListIcon from '@mui/icons-material/FilterList'
import SearchIcon from '@mui/icons-material/Search'
import Button from '@mui/material/Button'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import { gridFilterModelSelector, gridFilteredSortedRowIdsSelector } from '@mui/x-data-grid-pro'
import React, { useCallback, useRef, useState } from 'react'

import T from 'T'

import type { Theme } from '@mui/material/styles'
import type { GridApiPro } from '@mui/x-data-grid-pro'

// Match the toolbar buttons: the theme only unsets textTransform on MuiButton.
const toggleSx = (theme: Theme) => ({
  m: 0,
  '& .MuiFormControlLabel-label': { ...theme.typography.button, color: 'primary.main', textTransform: 'none' },
})

type ActivityHistoryActionsProps = {
  apiRef: React.MutableRefObject<GridApiPro>
  // Hands the Filters button up so the grid can anchor its filter panel to it.
  onFilterAnchorChange?: (element: HTMLElement | null) => void
  showSequenceChangesToggle?: boolean
  sequenceChangesShown?: boolean
  onToggleSequenceChanges?: (event: React.ChangeEvent<HTMLInputElement>) => void
}

const ActivityHistoryActions = ({
  apiRef,
  onFilterAnchorChange,
  showSequenceChangesToggle = false,
  sequenceChangesShown = false,
  onToggleSequenceChanges,
}: ActivityHistoryActionsProps) => {
  const [search, setSearch] = useState('')
  const searchInputRef = useRef<HTMLInputElement>(null)

  // These sit in the accordion summary, whose click handler toggles the panel.
  const handleContainerClick = useCallback((event: React.MouseEvent) => event.stopPropagation(), [])

  // Otherwise the summary's own button takes the focus, and the next space collapses the accordion.
  const handleContainerMouseDown = useCallback((event: React.MouseEvent) => {
    const control = (event.target as HTMLElement).closest('input, button, .MuiSwitch-root')
    // contains(): closest() climbs past this row and finds the summary button itself.
    if (control && event.currentTarget.contains(control)) return
    event.preventDefault()
    searchInputRef.current?.focus()
  }, [])

  const handleShowFilters = useCallback(() => apiRef.current?.showFilterPanel(), [apiRef])

  // MUI drops hidden columns from the quick filter by default, and every value column here is hidden.
  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target
      setSearch(value)
      if (!apiRef.current) return
      apiRef.current.setFilterModel({
        ...gridFilterModelSelector(apiRef),
        quickFilterValues: value.split(' ').filter(Boolean),
        quickFilterExcludeHiddenColumns: false,
      })
    },
    [apiRef]
  )

  // allColumns reaches the hidden value columns; the visible one is excluded from export.
  const handleExport = useCallback(
    () =>
      apiRef.current?.exportDataAsCsv({
        allColumns: true,
        getRowsToExport: ({ apiRef: gridApiRef }) => gridFilteredSortedRowIdsSelector(gridApiRef),
      }),
    [apiRef]
  )

  return (
    <Stack alignItems="center" direction="row" spacing={1} onClick={handleContainerClick} onMouseDown={handleContainerMouseDown}>
      <Button ref={onFilterAnchorChange} startIcon={<FilterListIcon />} onClick={handleShowFilters}>
        {T.FILTERS}
      </Button>
      {showSequenceChangesToggle && (
        <FormControlLabel
          control={<Switch checked={sequenceChangesShown} size="small" onChange={onToggleSequenceChanges} />}
          label={T.SHOW_SEQUENCE_CHANGES}
          sx={toggleSx}
        />
      )}
      <TextField
        inputRef={searchInputRef}
        placeholder={T.SEARCH_PLACEHOLDER}
        size="small"
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
        value={search}
        variant="standard"
        onChange={handleSearchChange}
      />
      <IconButton aria-label={T.DOWNLOAD_CSV_TOOLTIP} title={T.DOWNLOAD_CSV_TOOLTIP} onClick={handleExport}>
        <FileDownloadOutlinedIcon />
      </IconButton>
    </Stack>
  )
}

export default ActivityHistoryActions
